import { toKenyanMobile } from '@chukua/common';
import { useState } from 'react';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { useConfirmation } from '../../../hooks/use-confirmation';
import { useRefreshCurrentUser } from '../../../hooks/use-refresh-current-user';
import { CONTACT_COPY, contactChangeError, type ContactKind } from '../policies/contact-change';
import { completeEmailChange, completePhoneChange, startEmailChange, startPhoneChange } from '../profile.api';

/** `displayedCode` is set only on a test deployment that shows codes instead of sending them. */
export type ContactChangeState = { phase: 'idle' } | { phase: 'new' } | { phase: 'code'; value: string; displayedCode: string | null };

const API: Record<ContactKind, { start: (value: string) => Promise<{ displayedCode: string | null }>; complete: (code: string) => Promise<void> }> = {
  email: { start: startEmailChange, complete: completeEmailChange },
  phone: { start: (phone) => startPhoneChange(toKenyanMobile(phone) ?? phone), complete: completePhoneChange },
};

/**
 * Changing the email or mobile: the new one is typed, a code goes to it (after "Confirm it's you" when the session needs it),
 * and the change is saved once that code comes back, so nobody can move their sign-in to an address they can't read.
 */
export function useContactChange(kind: ContactKind) {
  const [state, setState] = useState<ContactChangeState>({ phase: 'idle' });
  const { error, submitting, submit, clearError } = useSubmission();
  const confirmation = useConfirmation();
  const refreshUser = useRefreshCurrentUser();
  const toast = useToast();
  const copy = CONTACT_COPY[kind];

  async function sendCode(value: string): Promise<boolean> {
    const sent = await submit(
      () => confirmation.confirmed(copy.purpose, () => API[kind].start(value)),
      (err) => contactChangeError(kind, err),
    );
    if (!sent) return false;
    setState({ phase: 'code', value, displayedCode: sent.displayedCode });
    return true;
  }

  async function resend() {
    if (state.phase !== 'code') return;
    if (await sendCode(state.value)) toast.success('New code sent');
  }

  async function verify(code: string) {
    const isChanged = await submit(
      () => confirmation.confirmed(copy.purpose, () => API[kind].complete(code).then(() => true)),
      (err) => contactChangeError(kind, err),
    );
    if (!isChanged) return;
    await refreshUser();
    setState({ phase: 'idle' });
    toast.success(copy.changed);
  }

  function close() {
    setState({ phase: 'idle' });
    clearError();
  }

  return { state, error, submitting, confirmation, open: () => setState({ phase: 'new' }), sendCode, resend, verify, close };
}
